import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faUserClock, faEnvelope, faPhone } from "@fortawesome/pro-regular-svg-icons";

import { useAuthContext } from "../../contexts/AuthContext";
import { isStringEmpty, parseStringOr } from "../App/AppxUtils";
import AppxLangAvatar from "../AppxLangAvatar";
import HamburgerMenu from "../HamburgerMenu";
import Loading from "../Loading";
import { PUBLIC_URL } from "../../config";

import "./index.css";


const { Content, Footer, Sider } = Layout;


function HeaderInfo() {
    const { t } = useTranslation('appxweb');
    const { themeConfig } = useAuthContext();
    const address = themeConfig.address || null;
    const timetable = themeConfig.timetable || {};
    const timetable1st = timetable.first || null;
    const timetable2nd = timetable.second || null;
    const contact = themeConfig.contact || null;
    let headerInfoBottomList = [];

    if (contact != null) {
        const email = parseStringOr(contact.email, "");
        const phone = parseStringOr(contact.phone, "");

        if (email !== "") {
            headerInfoBottomList.push(
                <Fragment>
                    <FontAwesomeIcon className="hiIcon" icon={faEnvelope} />
                    <a href={`mailto:${email}`}>{email}</a>
                </Fragment>
            );
        }

        if (phone !== "") {
            headerInfoBottomList.push(
                <Fragment>
                    <FontAwesomeIcon className="hiIcon" icon={faPhone} />
                    <a href={`tel:${phone}`}>{phone}</a>
                </Fragment>
            );
        }
    }

    return (
        <div className="header-info">
            <ul>
                {address != null && (
                <li>
                    <FontAwesomeIcon className="hiIcon" icon={faMapMarkerAlt} />
                    <div className="hiInfo">
                        <span className="hiInfo-small">{address.small || ""}</span>
                        <span className="hiInfo-big">{address.big || ""}</span>
                    </div>
                </li>
                )}
                {timetable1st != null && (
                <li>
                    <FontAwesomeIcon className="hiIcon" icon={faUserClock} />
                    <div className="hiInfo">
                        <span className="hiInfo-small">{timetable1st.small || ""}</span>
                        <span className="hiInfo-big">{timetable1st.big || ""}</span>
                    </div>
                </li>
                )}
                {timetable2nd != null && (
                <li>
                    <div className="hiInfo">
                        <span className="hiInfo-small">{timetable2nd.small || ""}</span>
                        <span className="hiInfo-big">{timetable2nd.big || ""}</span>
                    </div>
                </li>
                )}
                {1 === 2 && (
                <li>
                    <img alt="sunIcon" src={`${PUBLIC_URL}/icons/sun.png`} />
                    <div className="hiInfo">
                        <span className="hiInfo-small">{t('Header.timetable_1')}</span>
                        <span className="hiInfo-big">{t('Header.timetable_2')}</span>
                    </div>
                </li>
                )}
                <li>
                    <AppxLangAvatar />
                </li>
            </ul>
            {headerInfoBottomList.length > 0 && (
            <ul className="header-info-bottom-list">
                {headerInfoBottomList.map((item, idx) => (
                <li key={`header-info-bottom-list-${idx}`}>{item}</li>
                ))}
            </ul>
            )}
        </div>
    );
}

function PageView(props) {
    const { t, i18n } = useTranslation('appxweb');
    const { org, theme, themeConfig, config } = useAuthContext();
    const [ collapsed, setCollapsed ] = useState(true);
    const handleHamburgerClick = () => setCollapsed(!collapsed);
    const onBreakpoint = (broken) => {
        if (!broken) {
            setCollapsed(true);
        }
    };
    let footerStyle = null;

    if (!isStringEmpty(themeConfig?.images?.footer)) {
        footerStyle = {
            backgroundImage: `url(${PUBLIC_URL + themeConfig.images.footer})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
        };
    }

    return (
        <Layout
            className={theme}
        >
            <Sider
                className="upi-sider"
                trigger={null}
                collapsible
                collapsed={collapsed}
                breakpoint="lg"
                onBreakpoint={onBreakpoint}
                width={320}
                collapsedWidth="0">
                <div className="upi-sider-logobox">
                    {!isStringEmpty(themeConfig?.logos?.header) && (
                    <img className="logo" alt="logo" src={PUBLIC_URL + themeConfig.logos.header} />
                    )}
                </div>
                <HeaderInfo />
            </Sider>
            <Layout className="layout">
                <header className="upi-header">
                    <div className="upi-header-content">
                        <div className="hamburger-menu-bar">
                            <HamburgerMenu
                                menuClicked={handleHamburgerClick}
                                className="hamburger-menu"
                                isOpen={(!collapsed)}
                                animationDuration={0.1}
                                />
                        </div>
                        {(collapsed && !isStringEmpty(themeConfig?.logos?.header)) && (
                        <img className="logo" alt="logo" src={PUBLIC_URL + themeConfig.logos.header} />
                        )}
                        <HeaderInfo />
                    </div>
                </header>
                <Content>
                    {props.loading ? (
                    <div className="site-page-content" style={{textAlign: 'center'}}>
                        <Loading />
                    </div>
                    ) : (
                    <div className="site-page-content">
                        {props.leadText && (
                        <div className="pv-leadtext">
                            <div className="pv-leadtext-line">
                                <span></span>
                            </div>
                            <div className="pv-leadtext-text">
                                <p>{props.leadText}</p>
                            </div>
                        </div>
                        )}
                        {props.title && (<h1 className="pv-title">{props.title}</h1>)}
                        {props.subTitle && (<p className="pv-subtitle">{props.subTitle}</p>)}
                        {props.notes && (<p className="pv-notes">{props.notes}</p>)}
                        <div className="pv-divider" />
                        {props.children}
                    </div>
                    )}
                </Content>
                <Footer
                    className="upi-footer"
                    style={footerStyle}
                >
                    {config.hasChannels && (
                    <div className="upi-footer-top">
                        <p>
                            <a href={`${config.channelChooseUrl}/${org}/list?lang=${i18n.language}`}>
                                &gt; {t('Footer.See Channel List')}
                            </a>
                        </p>
                    </div>
                    )}
                    {!isStringEmpty(themeConfig?.logos?.footer) && (
                    <img className="footer-logo" alt="footerLogo" src={PUBLIC_URL + themeConfig.logos.footer} />
                    )}
                    {!isStringEmpty(themeConfig?.content?.footer) && (
                    <div className="upi-footer-content" dangerouslySetInnerHTML={{__html: themeConfig.content.footer}} />
                    )}
                </Footer>
            </Layout>
        </Layout>
    );
}

function PageViewSection(props) {
    let className = "pv-section";

    if (props.className) {
        className += ' ' + props.className;
    }

    return (
        <div className={className}>
            {props.section && (<h3>{props.section}</h3>)}
            {props.title && (<h2>{props.title}</h2>)}
            {props.legend && (<p dangerouslySetInnerHTML={{__html: props.legend}} />)}
            <div
                className="pv-section-content"
                style={props.style}>
                {props.children}
            </div>
        </div>
    );
}

export {
    PageView,
    PageViewSection,
};
